import axios from 'axios';
import { buildReturnTo } from '../lib/auth/navigation';
import type { ParteResumivel } from '../lib/partes/resumo';
import type { TagResumo } from './tags';
import { setSentryRequestId } from '../lib/observability/sentry';
import { setLastRequestId } from '../services/errorTracking';
import type { DocumentoForense } from '@/components/editor-forense';

// Em dev, usa path relativo /api/v1 (funciona via Vite proxy e Tailscale sem mixed content).
// Em produção, definir VITE_API_BASE_URL com a URL absoluta da API.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

export const api = axios.create({
  baseURL: API_BASE_URL,
  // Lista vai como chave repetida (`tag_ids=a&tag_ids=b`), que é o que o FastAPI lê;
  // o padrão do axios (`tag_ids[]=a`) chegaria vazio.
  paramsSerializer: { indexes: null },
});

/**
 * Tenta fazer JSON.parse de strings que começam com '{' ou '['.
 * Axios já parseia automaticamente respostas com Content-Type `application/json`,
 * mas o backend usa `application/problem+json` (RFC 7807) para erros — em
 * navegadores/configurações onde o auto-parse fica condicionado ao content-type
 * exato, isso fazia `response.data` chegar como string e quebrava checagens
 * tipo `data.code === 'whitelist_required'`. Esta função é idempotente: se
 * já vier objeto/array, devolve sem mexer.
 */
function tryParseJsonString<T>(data: T): T {
  if (typeof data !== 'string') return data;
  const trimmed = data.trim();
  if (trimmed.length === 0) return data;
  const first = trimmed[0];
  if (first !== '{' && first !== '[') return data;
  try {
    return JSON.parse(trimmed) as T;
  } catch {
    return data;
  }
}

// Interceptor para tratamento de erros (sem localStorage - Auth0 gerencia tokens)
api.interceptors.response.use(
  (response) => {
    // Capturar X-Request-Id para correlação de erros
    const requestId = response.headers?.['x-request-id'];
    if (requestId) {
      setLastRequestId(requestId);
      setSentryRequestId(requestId);
    }
    response.data = tryParseJsonString(response.data);
    return response;
  },
  (error) => {
    // Captura o X-Request-Id da resposta de erro também — sem isso, a tag
    // request_id no Sentry permanece com o ID da última chamada bem-sucedida
    // e a correlação Sentry↔CloudWatch falha justamente onde mais importa.
    const requestId = error?.response?.headers?.['x-request-id'];
    if (requestId) {
      setLastRequestId(requestId);
      setSentryRequestId(requestId);
    }
    // Garantir que respostas problem+json (RFC 7807) sejam parseadas mesmo
    // quando o Content-Type não casa exatamente com `application/json`.
    if (error?.response && 'data' in error.response) {
      error.response.data = tryParseJsonString(error.response.data);
    }
    if (error.response?.status >= 500) {
      error.friendlyMessage = 'Ocorreu um erro no servidor. Tente novamente mais tarde.';
    }
    return Promise.reject(error);
  }
);

export interface Processo {
  id: string;
  numero: string;
  tribunal: string;
  classe: string;
  /** Assunto principal do snapshot processual. Ausente em processos antigos. */
  assunto?: string | null;
  status: string;
  tipo: string;
  possui_permissao_peticionar: boolean;
  created_at?: string;
  /** DJEN (intimação), DATALAKE (importado do acervo), MANUAL, PJE. */
  origem?: string | null;
  /** Partes cruas (polo + nome) para o resumo "Fulano x Beltrano e outros (2)". */
  partes_resumo?: ParteResumivel[];
  /** "Acompanho de perto" do titular do acervo (estrela no Acervo e nos autos). */
  favorito?: boolean;
  /** Prazo em dobro marcado pelo advogado (CPC 180, 183, 186, 229): entra na conta. */
  prazo_em_dobro?: boolean;
  /** Onde o processo está hoje, deduzido pelo backend (services/instancia.py); null: sem sinal. */
  instancia?: 'primeiro_grau' | 'segundo_grau' | 'superior' | null;
  /** O primeiro vencimento, a partir de 7 dias atrás (os vencidos da semana também). Só na lista. */
  /** ``proximo``: o primeiro de hoje em diante (a ordem do acervo); ``data`` pode ter vencido. */
  vencimento?: { data: string; estimado: boolean; proximo?: string | null } | null;
  /** A próxima audiência ou sessão de julgamento, da agenda. Só na lista. */
  audiencia?: { data_inicio: string; hora_conhecida: boolean; tipo: string } | null;
  /** Tags que quem consulta vê (PLANO_TAGS). */
  tags?: TagResumo[];
  /**
   * Visão do escritório (docs/PLANO_COMPARTILHAMENTO_ESCRITORIO.md A7). Ausente = DONO
   * (resposta de antes do campo, ou de quem não tem escritório).
   */
  acesso?: NivelDeAcesso;
  /** O dono do processo, quando não é quem consulta. */
  dono?: UsuarioResumo | null;
  /** Advogados do escritório com este CNJ (o selo). Quem consulta vem primeiro. */
  advogados?: UsuarioResumo[];
  /** Tags de OUTRAS linhas do mesmo CNJ (de colegas): só para ver, não se editam aqui. */
  tags_outras_linhas?: TagResumo[];
  /** Quem cuida do processo no escritório (§19). Vazio: ninguém distribuiu, cuida o dono. */
  responsaveis?: ResponsavelResumo[];
  /** É de quem consulta cuidar. Ausente: o acervo próprio, sem escritório (sempre). */
  sob_meus_cuidados?: boolean;
  /** Quem consulta edita e arquiva esta linha (o dono, o gestor do dono, quem cuida). */
  pode_editar?: boolean;
}

export interface ResponsavelResumo {
  tipo: 'USUARIO' | 'EQUIPE';
  id: string;
  nome: string;
}

/** O que quem consulta pode fazer no registro: o dele, compartilhado com ele, ou só ler. */
export type NivelDeAcesso = 'DONO' | 'COLABORACAO' | 'LEITURA';

export interface UsuarioResumo {
  id: string;
  nome: string;
  /** Saiu do escritório: o registro é do arquivo do escritório, em leitura. */
  ex_membro?: boolean;
}

/** Todos / Meus / Escritório: a visão do escritório nas listas (aplicada antes dos filtros). */
export type OrigemDaVisao = 'todos' | 'meus' | 'escritorio';

/** O registro é de um colega? (``acesso`` ausente é do próprio usuário.) */
export function eDeColega(registro: { acesso?: NivelDeAcesso }): boolean {
  return (registro.acesso ?? 'DONO') !== 'DONO';
}

/** O registro é do usuário ou de um colega em que ele colabora (A3: prepara — prazo, tags,
 * marcar como lida). Em Leitura (retido, supervisão), não. */
export function podePreparar(registro: { acesso?: NivelDeAcesso }): boolean {
  return (registro.acesso ?? 'DONO') !== 'LEITURA';
}

export async function fetchProcessos(params?: { origem?: OrigemDaVisao }) {
  const { data } = await api.get<{ items: Processo[] }>('/processos', { params });
  return data.items;
}

export async function createProcesso(payload: Partial<Processo>) {
  const { data } = await api.post<Processo>('/processos', payload);
  return data;
}

export async function updateProcesso(id: string, payload: Partial<Processo>) {
  const { data } = await api.put<Processo>(`/processos/${id}`, payload);
  return data;
}

/** PUT com o VALOR desejado (nunca alterna): o último toque vence. Idempotente. */
export async function setProcessoFavorito(id: string, favorito: boolean) {
  const { data } = await api.put<Processo>(`/processos/${id}/favorito`, { favorito });
  return data;
}

/** Prazo em dobro do processo: PUT com o valor desejado; o servidor refaz os vencimentos. */
export async function setProcessoPrazoEmDobro(id: string, prazoEmDobro: boolean) {
  const { data } = await api.put<Processo>(`/processos/${id}/prazo-em-dobro`, { prazo_em_dobro: prazoEmDobro });
  return data;
}

export interface Documento {
  id: string;
  processo_id: string;
  nome: string;
  mime: string;
  tamanho: number;
  hash: string;
  status_assinatura: string;
  origem: string;
  artefato_id?: string | null;
  created_at: string;
  assinatura_atual?: string | null;
}

export interface UploadInitResponse {
  upload_url: string;
  document_id: string;
  artefato_id: string;
  fields: Record<string, string>;
}

export async function fetchDocumentos(processoId: string) {
  const { data } = await api.get<Documento[]>('/documentos', {
    params: { processo_id: processoId }
  });
  return data;
}

export async function uploadDocumentoInit(payload: {
  processo_id: string;
  nome: string;
  mime: string;
  tamanho: number;
  hash: string;
}) {
  const { data } = await api.post<UploadInitResponse>('/documentos/upload-init', payload);
  return data;
}

export async function createPeticao(payload: { processo_id: string; corpo: string; anexos?: string[] }) {
  const { data } = await api.post('/peticoes', payload);
  return data;
}

export interface PeticaoModelo {
  id: string;
  titulo: string;
  corpo: string;
  variaveis?: Record<string, string>;
  usuario_id?: string | null;
  created_at?: string;
  escritorio_id?: string | null;
  escritorio_nome?: string | null;
  origem?: string;
  compartilhado_por?: string | null;
  finalidade?: string;
  aprovado_em?: string | null;
  /** Tags que quem consulta vê (PLANO_TAGS). */
  tags?: TagResumo[];
  /**
   * Papel, margens e timbrado da folha do editor forense. `null` = corpo no HTML
   * do editor antigo, que o editor converte ao abrir (docs/PLANO_EDITOR_TELAS.md).
   */
  documento?: DocumentoForense | null;
}

export async function fetchModelos(params?: { origem?: string; tag_ids?: string[] }) {
  const { data } = await api.get<PeticaoModelo[]>('/modelos-peticao', { params });
  return data;
}

export interface Tribunal {
  id: string;
  codigo_pje: string;
  nome: string;
  sigla: string;
  tipo: string;
  uf?: string | null;
  endpoint_mni?: string | null;
  endpoint_pje?: string | null;
  ativo: boolean;
  created_at?: string;
}

export async function fetchTribunais(params?: { ativo?: boolean }) {
  const { data } = await api.get<{ items: Tribunal[] }>('/tribunais', { params });
  return data.items;
}

export async function createModelo(payload: {
  titulo: string;
  corpo: string;
  variaveis: Record<string, string>;
  escritorio_id?: string;
  finalidade?: string;
  documento?: DocumentoForense;
}) {
  const { data } = await api.post<PeticaoModelo>('/modelos-peticao', payload);
  return data;
}

/**
 * Cópia PESSOAL de um modelo que o usuário enxerga mas não altera (do sistema,
 * compartilhado): é o "Editar" desses modelos. Mantém a finalidade.
 */
export async function copiarModelo(id: string) {
  const { data } = await api.post<PeticaoModelo>(`/modelos-peticao/${id}/copia`);
  return data;
}

export async function updateModelo(
  id: string,
  payload: { titulo?: string; corpo?: string; variaveis?: Record<string, string>; finalidade?: string; documento?: DocumentoForense }
) {
  const { data } = await api.put<PeticaoModelo>(`/modelos-peticao/${id}`, payload);
  return data;
}

export async function deleteModelo(id: string) {
  await api.delete(`/modelos-peticao/${id}`);
}

export interface Certificado {
  numero_serie: string;
  /** Rótulo pessoal dado pelo usuário (guardado no JurisApp); null quando não definido. */
  apelido?: string | null;
  nome: string;
  cpf_certificado: string;
  emissor: string;
  validade_inicio: string;
  validade_fim: string;
  key_type: string;
  ativo: boolean;
}

export interface CertificadoCreateResponse extends Certificado {
  cpf_atualizado: boolean;
}

export async function uploadCertificado(form: FormData): Promise<CertificadoCreateResponse> {
  const { data } = await api.post<CertificadoCreateResponse>('/certificados', form);
  return data;
}

/** Teto do apelido após o trim — o mesmo do backend (`APELIDO_MAX`). */
export const CERTIFICADO_APELIDO_MAX = 60;

/** Define o apelido do certificado; vazio remove. */
export async function updateCertificadoApelido(
  numeroSerie: string,
  apelido: string,
): Promise<{ numero_serie: string; apelido: string | null }> {
  const { data } = await api.patch<{ numero_serie: string; apelido: string | null }>(
    `/certificados/${encodeURIComponent(numeroSerie)}/apelido`,
    { apelido },
  );
  return data;
}

export async function fetchCertificados(): Promise<Certificado[]> {
  const { data } = await api.get<Certificado[]>('/certificados');
  return data;
}

export async function toggleCertificadoAtivo(
  numeroSerie: string,
  ativo: boolean,
): Promise<Certificado> {
  const { data } = await api.patch<Certificado>(
    `/certificados/${encodeURIComponent(numeroSerie)}/ativo`,
    { ativo },
  );
  return data;
}


export interface AppUser {
  id: string;
  nome: string;
  email: string;
  email_verified: boolean;
  role: string;
  picture_url: string | null;
  cpf: string | null;
  onboarding_completado: boolean;
  identidade_verificada: boolean;
  djen_ultimo_sync: string | null;
  has_active_certificate: boolean;
  /** Fim da validade do certificado ativo (ISO); ausente sem certificado ou com o autenticador fora. */
  certificate_valid_until?: string | null;
  oabs: Array<{ id: string; numero: string; uf: string; tipo: string; verificada: boolean }>;
}

export async function fetchMe(token?: string): Promise<AppUser> {
  const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  const { data } = await api.get<AppUser>('/auth/me', config);
  return data;
}

export async function checkEmailVerified(): Promise<{ verified: boolean }> {
  const { data } = await api.post<{ verified: boolean }>('/auth/sync-email-verified');
  return data;
}

export async function resendVerification(): Promise<{ status: string }> {
  const { data } = await api.post<{ status: string }>('/auth/resend-verification');
  return data;
}

// Calendar Events
export interface CalendarEvent {
  id: string;
  titulo: string;
  descricao?: string;
  tipo: string;
  data_inicio: string;
  data_fim?: string;
  /** `false`: a intimação não disse a hora, e `data_inicio` é só a âncora do
   *  dia (meia-noite local). Ausente vale como conhecida. */
  hora_conhecida?: boolean;
  /** `djen` (criado pela leitura das intimações) ou `usuario`. */
  origem?: string;
  processo_id?: string;
  comunicacao_id?: string;
  local?: string;
  lembrete_minutos?: number;
  google_event_id?: string;
  created_at?: string;
  updated_at?: string;
  /** A sessão de julgamento lida do texto da intimação de pauta (backend,
   *  `services/pauta_sessao.py`). Ausente = o texto não diz. */
  sessao_tribunal?: string | null;
  sessao_colegiado?: string | null;
  sessao_modalidade?: string | null;
  sessao_relator?: string | null;
  processo_numero?: string | null;
  processo_classe?: string | null;
  /** Primeiro destinatário da intimação — só para a tela (não vai ao título
   *  nem ao Google Calendar). */
  parte_principal?: string | null;
  /** Evento da agenda de um colega (processo de que o usuário cuida, Bloco 5 do
   *  compartilhamento no escritório). Ausente: é do próprio usuário. */
  dono?: { id: string; nome: string } | null;
}

export async function fetchCalendarEvents(
  startDate?: string,
  endDate?: string,
  comunicacaoId?: string,
  limit?: number,
  tipo?: string,
) {
  const { data } = await api.get<CalendarEvent[]>('/calendario/eventos', {
    params: { data_inicio: startDate, data_fim: endDate, comunicacao_id: comunicacaoId, limit, tipo }
  });
  return data;
}

export async function createCalendarEvent(payload: Omit<CalendarEvent, 'id' | 'created_at' | 'updated_at'>) {
  const { data } = await api.post<CalendarEvent>('/calendario/eventos', payload);
  return data;
}

/** `processo_id: null` desfaz o vínculo; ausente, o servidor mantém o que há. */
export type CalendarEventUpdate = Omit<Partial<CalendarEvent>, 'processo_id'> & { processo_id?: string | null };

export async function updateCalendarEvent(id: string, payload: CalendarEventUpdate) {
  const { data } = await api.put<CalendarEvent>(`/calendario/eventos/${id}`, payload);
  return data;
}

export async function deleteCalendarEvent(id: string) {
  await api.delete(`/calendario/eventos/${id}`);
}

// Google Calendar Integration
export interface GoogleCalendarStatus {
  connected: boolean;
  email?: string;
  sync_enabled?: boolean;
  last_sync?: string;
}

export async function fetchGoogleCalendarStatus() {
  const { data } = await api.get<GoogleCalendarStatus>('/calendario/google/status');
  return data;
}

export async function connectGoogleCalendar() {
  // Salvar rota atual para retornar após o fluxo OAuth completo
  // (Google redirect → backend callback → Auth0 re-auth → volta aqui)
  sessionStorage.setItem('jurisapp:returnTo', buildReturnTo(window.location));
  const { data } = await api.get<{ auth_url: string }>('/calendario/google/connect', {
    params: { origin: window.location.origin },
  });
  return data;
}

export async function disconnectGoogleCalendar() {
  await api.post('/calendario/google/disconnect');
}

export async function syncGoogleCalendar() {
  const { data } = await api.post<{ synced_events: number }>('/calendario/google/sync');
  return data;
}

// --- Tipos genéricos de paginação (reutilizáveis por todas as entidades) ---

export type SortDirection = 'asc' | 'desc';

export interface PaginationParams {
  page?: number;
  page_size?: number;
  order_by?: string;
  order_dir?: SortDirection;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

// Artefatos (standalone file management)
export interface Artefato {
  id: string;
  usuario_id: string;
  nome: string;
  descricao?: string | null;
  mime: string;
  tamanho: number;
  hash: string;
  local_s3: string;
  tipo: string;
  /** Nomes das tags visíveis (contrato antigo; o PWA com cache antigo lê este campo). */
  tags: string[];
  /** As mesmas tags com id, cor e categoria (PLANO_TAGS). */
  etiquetas?: TagResumo[];
  metadados: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  escritorio_id?: string | null;
  escritorio_nome?: string | null;
  origem?: string;
  compartilhado_por?: string | null;
  cliente_id?: string | null;
  cliente_nome?: string | null;
}

export interface ArtefatoCounts {
  total: number;
  documentos: number;
  imagens: number;
  outros: number;
  storage_bytes: number;
}

export async function fetchArtefatoCounts(params?: {
  search?: string;
  /** Rigor da busca inteligente no servidor: `strict` exige a palavra inteira. */
  rigor?: 'flexible' | 'strict';
  tag?: string;
  tag_ids?: string[];
  origem?: string;
}) {
  const { data } = await api.get<ArtefatoCounts>('/artefatos/counts', { params });
  return data;
}

export async function fetchArtefatos(params?: {
  tipo?: string;
  search?: string;
  rigor?: 'flexible' | 'strict';
  /** Nome da tag (filtro antigo). */
  tag?: string;
  /** Filtro facetado por tags (PLANO_TAGS D7). */
  tag_ids?: string[];
  origem?: string;
} & PaginationParams) {
  const { data } = await api.get<PaginatedResponse<Artefato>>('/artefatos', { params });
  return data;
}

export interface ArtefatoUploadInitResponse {
  upload_url: string;
  artefato_id: string;
  fields: Record<string, string>;
  is_duplicate: boolean;
}

export async function uploadArtefatoInit(payload: {
  nome: string;
  mime: string;
  tamanho: number;
  hash: string;
  tipo?: string;
  descricao?: string;
  /** Nomes (contrato antigo). */
  tags?: string[];
  /** Ids das tags escolhidas (PLANO_TAGS). */
  tag_ids?: string[];
  escritorio_id?: string;
  cliente_id?: string;
}) {
  const { data } = await api.post<ArtefatoUploadInitResponse>('/artefatos/upload-init', payload);
  return data;
}

export async function downloadArtefato(id: string) {
  const { data } = await api.get<{ download_url: string }>(`/artefatos/${id}/download`);
  return data;
}

export async function previewArtefato(id: string) {
  const { data } = await api.get<{ download_url: string }>(`/artefatos/${id}/preview`);
  return data;
}

export async function updateArtefato(id: string, payload: {
  descricao?: string;
  tags?: string[];
  cliente_id?: string | null;
}) {
  const { data } = await api.put<Artefato>(`/artefatos/${id}`, payload);
  return data;
}

export async function deleteArtefato(id: string) {
  await api.delete(`/artefatos/${id}`);
}

export interface AnexarAProcessoResponse {
  documento_id: string;
  artefato_id: string;
  processo_id: string;
}

export async function anexarArtefatoAProcesso(artefatoId: string, processoId: string) {
  const { data } = await api.post<AnexarAProcessoResponse>(
    `/artefatos/${artefatoId}/anexar-a-processo`,
    { processo_id: processoId }
  );
  return data;
}

// Stripe / Subscriptions
export async function createSubscriptionCheckout(planCode: string, addons: string[] = []): Promise<{
  client_secret: string;
  subscription_id: string;
}> {
  const response = await api.post('/subscriptions/create-checkout', { plan_code: planCode, addons });
  return response.data;
}

export async function confirmCheckout(sessionId: string) {
  const response = await api.post(`/subscriptions/confirm-checkout?session_id=${encodeURIComponent(sessionId)}`);
  return response.data;
}

export async function getStripeConfig(): Promise<{ publishable_key: string }> {
  const response = await api.get('/subscriptions/config');
  return response.data;
}

export async function cancelSubscription(): Promise<{ status: string }> {
  const response = await api.post('/subscriptions/cancel');
  return response.data;
}

// OABs
export async function addOab(data: { numero: string; uf: string; tipo?: string }): Promise<unknown> {
  const { data: result } = await api.post('/auth/me/oabs', data);
  return result;
}

// Onboarding
export async function completeOnboarding(data: {
  oabs?: Array<{ numero: string; uf: string; tipo?: string }>;
  cpf?: string;
  nome?: string;
  plano_codigo?: string;
  identidade_verificada?: boolean;
}): Promise<{ status: string }> {
  const response = await api.post('/auth/onboarding/complete', data);
  return response.data;
}

// DJEN
export async function triggerDjenIngest(): Promise<{ status: string; outbox_event_id?: string; oabs_count?: number }> {
  const { data } = await api.post('/djen/ingest/trigger');
  return data;
}

export async function fetchDjenStatus(): Promise<{ status: string; comunicacoes?: number; processos?: number; error?: string }> {
  const { data } = await api.get('/djen/ingest/status');
  return data;
}

// Comunicações Processuais
export interface ExtractedDate {
  data: string;
  hora: string | null;
  /** Data final do intervalo (ISO yyyy-MM-dd) quando o texto define um range. */
  data_fim: string | null;
  /** Hora final do intervalo (HH:mm). Herda `hora` quando não explícita. */
  hora_fim: string | null;
  tipo: string;
  contexto: string;
  posicao: number;
}

export interface ExtractedPrazo {
  dias: number;
  unidade: "dias" | "dias_uteis" | "horas" | "meses" | "anos";
  contexto: string;
  contexto_inicio: number;
  posicao: number;
  tamanho: number;
  /** Lido por extenso ("cinco dias", "15 (quinze dias)"): só é o prazo para agir sem outro. */
  por_extenso?: boolean;
}

export interface ComunicacaoFonte {
  id: string;
  fonte: string;
  fonte_id: string;
  campos_preenchidos: string[];
  importado_em: string;
}

/** A conta do vencimento feita pelo sistema (backend: services/prazos/vencimento.py). */
export interface PrazoCalculo {
  /** `presumido`: o texto não diz o prazo; é o principal da lei para o ato (prazo_presumido). */
  origem: 'calculado' | 'presumido';
  /** Só no presumido: a providência, o dispositivo e o ato. */
  providencia?: string;
  dispositivo?: string;
  condicional?: boolean;
  ato_rotulo?: string;
  /** Decidido pela leitura da IA (Bloco 4): o número ancorado no texto ou na tabela. */
  ia?: boolean;
  /** A IA ou as travas pedem conferência — os motivos dizem por quê. */
  confira?: boolean;
  motivos_ia?: string[];
  /** Só no prazo do texto pela IA: o trecho que ela citou (existe no texto). */
  trecho?: string | null;
  /** O juiz fixou a data ("até 23.10", "de 14/12 a 24/12"): o vencimento é ela, sem
   *  contagem de dias — `dias`, `dias_efetivos` e `regime` vêm nulos. */
  data_fixada?: boolean;
  vencimento: string;
  publicacao: string | null;
  inicio: string;
  dias: number | null;
  dias_efetivos: number | null;
  regime: 'uteis' | 'corridos' | null;
  /** O texto disse só "dias": úteis por presunção do CPC 219, com aviso. */
  regime_presumido: boolean;
  aviso_regime: string | null;
  em_dobro: boolean;
  /** Posição, no texto, do prazo que foi contado (casa com ExtractedPrazo.posicao). */
  posicao: number | null;
  /** Faltou o lugar completo: feriados locais podem não ter entrado. */
  estimado: boolean;
  motivo_estimado: string | null;
  calendario_versao: string;
  passos: string[];
  /** Onde o ato é praticado: o formulário de "faltou um feriado" parte dele. */
  lugar?: {
    justica: string | null;
    tribunal: string | null;
    uf: string | null;
    municipio_ibge: number | null;
    segundo_grau: boolean;
  };
  /** Feriados e suspensões lançados nesta conta que entraram na conta. */
  particulares?: string[];
}

/** O estado do prazo da intimação (backend: services/prazos/presumido.py). */
export type PrazoEstado = 'texto' | 'presumido' | 'sem_prazo' | 'evento' | 'indefinido';

/** Um prazo que a lei dá ao ato, quando o texto não diz o prazo. */
export interface PrazoLegalPresumido {
  providencia: string;
  dias: number;
  regime: 'uteis' | 'corridos';
  dispositivo: string;
  /** Só cabe em certas hipóteses: a tela diz "se cabível". */
  condicional?: boolean;
  /** O que vira o vencimento da intimação (régua e agenda); os embargos de declaração, não. */
  principal?: boolean;
  vencimento?: string;
}

/** O ato lido no texto e, no presumido, os prazos da lei com os vencimentos. */
export interface PrazoPresumido {
  versao: number;
  ato: string;
  ato_rotulo: string;
  /** Decidido pela leitura da IA (Bloco 4). */
  ia?: boolean;
  confira?: boolean;
  motivos_ia?: string[];
  ato_detalhe?: string;
  /** O trecho do texto que decidiu o ato. */
  trecho?: { posicao: number; tamanho: number };
  rito?: string;
  rito_rotulo?: string;
  instancia?: string;
  prazos?: PrazoLegalPresumido[];
  ressalvas?: string[];
  /** Por que não há prazo presumido (indefinido). */
  motivo?: string;
  /** No penal, a conta não desconta recesso e suspensões (CPP 798-A). */
  suspensoes?: boolean;
  em_dobro?: boolean;
  estimado?: boolean;
  motivo_estimado?: string | null;
  calendario_versao?: string;
}

export interface ComunicacaoProcessualItem {
  id: string;
  processo_id: string | null;
  numero_processo: string;
  user_id: string;
  tipo: string | null;
  status_ciencia: string | null;
  data_disponibilizacao: string | null;
  data_ciencia: string | null;
  prazo_resposta: string | null;
  /** Como o prazo_resposta foi calculado pelo sistema (ausente: data de outra origem). */
  prazo_calculo?: PrazoCalculo | null;
  /** O estado do prazo; ausente ou null: ainda não classificada. */
  prazo_estado?: PrazoEstado | null;
  prazo_presumido?: PrazoPresumido | null;
  sigla_tribunal: string | null;
  nome_orgao: string | null;
  codigo_classe: string | null;
  nome_classe: string | null;
  assunto: string | null;
  texto: string | null;
  link: string | null;
  /** Partes do DJEN: `[{nome, polo, ...}]`. Fonte do resumo nas comunicações. */
  destinatarios_json: ParteResumivel[] | null;
  advogados_json: unknown;
  datas_extraidas: ExtractedDate[] | null;
  prazos_extraidos: ExtractedPrazo[] | null;
  fontes: ComunicacaoFonte[];
  lida: boolean;
  /** Quem marcou, quando foi um delegado na conta do titular (docs/PLANO_DELEGACAO.md). */
  lida_por_nome?: string | null;
  /** Evento de prazo já cadastrado na agenda para esta comunicação (se houver). */
  evento_prazo: { id: string; data_inicio: string } | null;
  /** Visão do escritório: ausente = do próprio usuário. */
  acesso?: NivelDeAcesso;
  /** O destinatário, quando a comunicação é de um colega ("recebida por"). */
  recebida_por?: UsuarioResumo | null;
  created_at: string;
  updated_at: string;
}

export interface ComunicacoesStats {
  total: number;
  nao_lidas: number;
  novas_hoje: number;
  /** Siglas de tribunal distintas — opções do filtro de tribunal. */
  tribunais: string[];
}

export async function fetchComunicacoes(params?: {
  lida?: boolean;
  search?: string;
  /** Rigor da busca inteligente no servidor: `strict` exige a palavra inteira. */
  rigor?: 'flexible' | 'strict';
  tribunal?: string;
  com_prazo?: boolean;
  novas_hoje?: boolean;
  sort?: 'recentes' | 'antigas' | 'nao-lidas';
  limit?: number;
  offset?: number;
  /** Visão do escritório; sem ela, só as do próprio usuário (padrão do backend). */
  origem?: OrigemDaVisao;
}): Promise<{ items: ComunicacaoProcessualItem[]; total: number }> {
  const { data } = await api.get('/comunicacoes', { params });
  return data;
}

export async function fetchComunicacao(id: string): Promise<ComunicacaoProcessualItem> {
  const { data } = await api.get(`/comunicacoes/${id}`);
  return data;
}

export async function fetchComunicacoesStats(): Promise<ComunicacoesStats> {
  const { data } = await api.get('/comunicacoes/stats');
  return data;
}

/** Sem ``lida``, alterna (os chamadores antigos). Com ele, o estado desejado: idempotente, e
 * dois atores no mesmo registro (o dono e quem cuida, Bloco 3) não desfazem a leitura um do outro. */
export async function markComunicacaoLida(id: string, lida?: boolean): Promise<{ id: string; lida: boolean }> {
  const { data } = await api.patch(`/comunicacoes/${id}/lida`, lida === undefined ? undefined : { lida });
  return data;
}

export async function markAllComunicacoesLidas(): Promise<{ updated: number }> {
  const { data } = await api.patch('/comunicacoes/mark-all-read');
  return data;
}

// Dashboard Stats
export interface DashboardStats {
  comunicacoes_nao_lidas: number;
  comunicacoes_novas_hoje: number;
  processos_ativos: number;
  processos_total: number;
  djen_ultimo_sync: string | null;
  djen_ultimo_status: string | null;
}

export async function fetchDashboardStats(): Promise<DashboardStats> {
  const { data } = await api.get('/dashboard/stats');
  return data;
}

// Push Notifications
export async function fetchVapidPublicKey(): Promise<{ public_key: string }> {
  const { data } = await api.get<{ public_key: string }>('/push/vapid-public-key');
  return data;
}

export async function subscribePush(subscription: {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  user_agent?: string;
}) {
  const { data } = await api.post('/push/subscribe', subscription);
  return data;
}

export async function unsubscribePush(endpoint: string) {
  await api.delete('/push/unsubscribe', { params: { endpoint } });
}

export async function fetchPushSubscriptions() {
  const { data } = await api.get('/push/subscriptions');
  return data;
}

// Escritórios
export interface Escritorio {
  id: string;
  nome: string;
  descricao?: string | null;
  /** O padrão das peças (timbrado, página, Corpo da peça) vence o de cada membro; senão, só sugere. */
  padrao_obrigatorio?: boolean;
  membros: EscritorioMembro[];
  created_at: string;
  updated_at: string;
}

export interface EscritorioResumo {
  id: string;
  nome: string;
  descricao?: string | null;
  meu_role: string;
  /** Quantos membros o escritório tem (o card da lista). */
  total_membros?: number;
  /** Distribui os processos: o Proprietário ou quem ele marcou (§19). */
  sou_gestor?: boolean;
}

export interface EscritorioMembro {
  id: string;
  usuario_id: string;
  usuario_nome: string;
  usuario_email: string;
  role: string;
  /** Proprietário, ou marcado por ele: distribui os processos (§19). */
  gestor?: boolean;
  created_at: string;
}

export interface EscritorioConvite {
  id: string;
  email: string;
  role: string;
  status: string;
  convidado_por_nome: string;
  created_at: string;
  expires_at: string;
  url_convite?: string | null;
}

export interface CompartilhamentoInfo {
  id: string;
  recurso_tipo: string;
  recurso_id: string;
  compartilhado_por_nome: string;
  compartilhado_por_email: string;
  compartilhado_com_nome: string;
  compartilhado_com_email: string;
  created_at: string;
}

export async function fetchEscritorios(): Promise<EscritorioResumo[]> {
  const { data } = await api.get<EscritorioResumo[]>('/escritorios');
  return data;
}

export async function fetchEscritorio(id: string): Promise<Escritorio> {
  const { data } = await api.get<Escritorio>(`/escritorios/${id}`);
  return data;
}

export async function createEscritorio(payload: { nome: string; descricao?: string }): Promise<Escritorio> {
  const { data } = await api.post<Escritorio>('/escritorios', payload);
  return data;
}

export async function updateEscritorio(id: string, payload: { nome?: string; descricao?: string }): Promise<Escritorio> {
  const { data } = await api.put<Escritorio>(`/escritorios/${id}`, payload);
  return data;
}

export async function definirPadraoDasPecas(id: string, obrigatorio: boolean): Promise<Escritorio> {
  const { data } = await api.put<Escritorio>(`/escritorios/${id}/padrao-das-pecas`, { padrao_obrigatorio: obrigatorio });
  return data;
}

export async function deleteEscritorio(id: string): Promise<void> {
  await api.delete(`/escritorios/${id}`);
}

export async function inviteToEscritorio(escId: string, payload: { email: string; role?: string }): Promise<EscritorioConvite> {
  const { data } = await api.post<EscritorioConvite>(`/escritorios/${escId}/convites`, payload);
  return data;
}

export async function fetchConvites(escId: string): Promise<EscritorioConvite[]> {
  const { data } = await api.get<EscritorioConvite[]>(`/escritorios/${escId}/convites`);
  return data;
}

export async function cancelConvite(escId: string, conviteId: string): Promise<void> {
  await api.delete(`/escritorios/${escId}/convites/${conviteId}`);
}

/** Convite que a pessoa recebeu e ainda não respondeu. */
export interface ConvitePendente {
  id: string;
  escritorio_id: string;
  escritorio_nome: string;
  role: string;
  convidado_por_nome: string;
  expires_at: string;
}

export async function fetchConvitesPendentes(): Promise<ConvitePendente[]> {
  const { data } = await api.get<ConvitePendente[]>('/escritorios/convites-pendentes');
  return data;
}

export async function aceitarConvitePendente(conviteId: string): Promise<{ escritorio_id: string; nome: string }> {
  const { data } = await api.post<{ escritorio_id: string; nome: string }>(
    `/escritorios/convites-pendentes/${conviteId}/aceitar`,
  );
  return data;
}

export async function recusarConvitePendente(conviteId: string): Promise<void> {
  await api.post(`/escritorios/convites-pendentes/${conviteId}/recusar`);
}

export async function aceitarConvite(token: string): Promise<{ escritorio_id: string; nome: string }> {
  const { data } = await api.post<{ escritorio_id: string; nome: string }>('/escritorios/aceitar-convite', { token });
  return data;
}

export async function updateMembroRole(escId: string, membroId: string, role: string): Promise<void> {
  await api.put(`/escritorios/${escId}/membros/${membroId}/role`, null, { params: { role } });
}

export async function removeMembro(escId: string, membroId: string): Promise<void> {
  await api.delete(`/escritorios/${escId}/membros/${membroId}`);
}

export async function compartilharRecurso(payload: { recurso_tipo: string; recurso_id: string; email: string }): Promise<CompartilhamentoInfo> {
  const { data } = await api.post<CompartilhamentoInfo>('/compartilhamentos', payload);
  return data;
}

export async function fetchCompartilhamentos(recursoTipo: string, recursoId: string): Promise<CompartilhamentoInfo[]> {
  const { data } = await api.get<CompartilhamentoInfo[]>('/compartilhamentos', { params: { recurso_tipo: recursoTipo, recurso_id: recursoId } });
  return data;
}

export async function removeCompartilhamento(id: string): Promise<void> {
  await api.delete(`/compartilhamentos/${id}`);
}

// ============ Planos Públicos (landing page) ============

export interface FeatureDisplay {
  text: string;
  included: boolean;
}

export interface PlanoPublic {
  id: string;
  codigo: string;
  nome: string;
  descricao: string | null;
  preco_centavos: number;
  intervalo: string;
  trial_dias: number | null;
  ativo: boolean;
  ordem_exibicao: number;
  grupo: string;
  features_display: FeatureDisplay[] | null;
  destaque: boolean;
  badge_text: string | null;
  cta_text: string | null;
}

/** Fetch simples para landing page (sem Axios/interceptors de auth). */
export async function fetchPlansPublic(): Promise<PlanoPublic[]> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';
  const res = await fetch(`${baseUrl}/subscriptions/plans`, { cache: 'no-store' });
  if (!res.ok) throw new Error('Falha ao carregar planos');
  return res.json();
}

/** Add-on vendido por cima do plano (hoje só o Secretário de IA). */
export interface AddonOferta {
  codigo: string;
  nome: string;
  preco_mensal_centavos: number;
  preco_anual_centavos: number;
  atendimentos_max: number;
  /** Há preço configurado no Stripe para contratar nesse intervalo. */
  disponivel_mensal: boolean;
  disponivel_anual: boolean;
}

/** Fetch simples, como o dos planos (a landing não tem sessão). */
export async function fetchAddonsPublic(): Promise<AddonOferta[]> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';
  const res = await fetch(`${baseUrl}/subscriptions/addons`, { cache: 'no-store' });
  if (!res.ok) throw new Error('Falha ao carregar os add-ons');
  return res.json();
}

export interface AddonAtivo {
  codigo: string;
  /** CORTESIA | STRIPE — só o contratado pelo Stripe se cancela pela tela. */
  origem: string;
  expira_em: string | null;
  /** O do escritório que esta conta paga; nulo = o pessoal. */
  escritorio_id?: string | null;
}

/** O pessoal; com ``escritorioId``, o do escritório (só o Proprietário). */
export async function contratarSecretario(escritorioId?: string): Promise<AddonAtivo> {
  const { data } = await api.post<AddonAtivo>('/subscriptions/addons/secretario', null, {
    params: escritorioId ? { escritorio_id: escritorioId } : undefined,
  });
  return data;
}

export async function cancelarSecretario(escritorioId?: string): Promise<void> {
  await api.delete('/subscriptions/addons/secretario', {
    params: escritorioId ? { escritorio_id: escritorioId } : undefined,
  });
}

export interface StripeConfig {
  publishable_key: string;
  stripe_configured: boolean;
}

export async function fetchStripeConfig(): Promise<StripeConfig> {
  const { data } = await api.get<StripeConfig>('/subscriptions/config');
  return data;
}

export async function changePlan(planCode: string): Promise<{ status: string; plan_code: string }> {
  const { data } = await api.post<{ status: string; plan_code: string }>('/subscriptions/change-plan', { plan_code: planCode });
  return data;
}

// ============ Admin: Planos ============

export interface PlanoAdmin {
  id: string;
  codigo: string;
  nome: string;
  descricao: string | null;
  preco_centavos: number;
  intervalo: string;
  trial_dias: number | null;
  limites: Record<string, unknown>;
  ativo: boolean;
  ordem_exibicao: number;
  stripe_price_id: string | null;
  grupo: string;
  features_display: FeatureDisplay[] | null;
  destaque: boolean;
  badge_text: string | null;
  cta_text: string | null;
  created_at: string;
  updated_at: string;
}

export async function fetchAdminPlans(): Promise<PlanoAdmin[]> {
  const { data } = await api.get<PlanoAdmin[]>('/admin/plans/');
  return data;
}

export async function createAdminPlan(payload: Partial<PlanoAdmin>): Promise<PlanoAdmin> {
  const { data } = await api.post<PlanoAdmin>('/admin/plans/', payload);
  return data;
}

export async function updateAdminPlan(id: string, payload: Partial<PlanoAdmin>): Promise<PlanoAdmin> {
  const { data } = await api.put<PlanoAdmin>(`/admin/plans/${id}`, payload);
  return data;
}

export async function toggleAdminPlan(id: string): Promise<PlanoAdmin> {
  const { data } = await api.patch<PlanoAdmin>(`/admin/plans/${id}/toggle`);
  return data;
}

// ============ Admin: Cortesia ============

export interface CortesiaOverride {
  id: string;
  user_id: string;
  user_nome: string | null;
  user_email: string | null;
  plano_grupo: string;
  concedido_por: string | null;
  motivo: string | null;
  ativo: boolean;
  expira_em: string | null;
  created_at: string;
  updated_at: string;
}

export async function fetchAdminCortesias(): Promise<CortesiaOverride[]> {
  const { data } = await api.get<CortesiaOverride[]>('/admin/cortesia/');
  return data;
}

export async function createAdminCortesia(payload: { user_id: string; plano_grupo: string; motivo?: string; expira_em?: string }): Promise<CortesiaOverride> {
  const { data } = await api.post<CortesiaOverride>('/admin/cortesia/', payload);
  return data;
}

export async function revokeAdminCortesia(id: string): Promise<void> {
  await api.delete(`/admin/cortesia/${id}`);
}

// ============ Admin: Features ============

export interface FeatureRegistryItem {
  id: string;
  codigo: string;
  nome: string;
  descricao: string | null;
  tipo: string;
  chave_limites: string;
  unidade: string | null;
  valor_nulo_significa: string;
  ordem: number;
  created_at: string;
}

export async function fetchAdminFeatures(): Promise<FeatureRegistryItem[]> {
  const { data } = await api.get<FeatureRegistryItem[]>('/admin/features/');
  return data;
}
